import { useEffect, useState } from 'react';
import { doc, getDoc } from 'firebase/firestore';
import { db } from '../config/firebase';

export interface CommentaryEntry {
  verseStart: number;
  verseEnd: number;
  date: string;
  reference: string;
  imageThumbUrl: string;
}

interface UseCommentaryEntriesResult {
  entries: CommentaryEntry[];
}

export const useCommentaryEntries = (
  lang: string | undefined,
  bookId: string | undefined,
  chapter: string | undefined
): UseCommentaryEntriesResult => {
  const [entries, setEntries] = useState<CommentaryEntry[]>([]);

  useEffect(() => {
    if (!lang || !bookId || !chapter) { setEntries([]); return; }
    let cancelled = false;

    // Normalize bookId to match Flutter format: "jn" → "Jn", "1jn" → "1Jn"
    const normalizeBookId = (id: string): string => {
      if (!id) return id;
      if (/^\d/.test(id)) return id[0] + id[1].toUpperCase() + id.slice(2).toLowerCase();
      return id[0].toUpperCase() + id.slice(1).toLowerCase();
    };

    const collectionName = `comments_index_${lang}`;
    const documentId = `${normalizeBookId(bookId)}_${chapter}`;

    const parseEntries = (data: Record<string, unknown>): CommentaryEntry[] => {
      const raw: unknown[] = Array.isArray(data.entries) ? data.entries : Object.values(data);
      return (raw as Record<string, unknown>[])
        .filter(item => typeof item === 'object' && item !== null)
        .map(item => ({
          verseStart:    Number(item.verseStart ?? 0),
          verseEnd:      Number(item.verseEnd ?? 0),
          date:          String(item.date ?? ''),
          reference:     String(item.reference ?? ''),
          imageThumbUrl: String(item.imageThumbUrl ?? item.image_thumb_url ?? ''),
        }))
        .filter(e => e.imageThumbUrl && e.date);
    };

    const docRef = doc(db, collectionName, documentId);
    getDoc(docRef)
      .then(async snap => {
        if (cancelled) return;
        if (snap.exists()) {
          const entries = parseEntries(snap.data());
          if (!cancelled) setEntries(entries);
          return;
        }
        // Fallback to FR when lang-specific index is missing (images are non-localized)
        if (lang !== 'fr') {
          const fallbackRef = doc(db, `comments_index_fr`, documentId);
          const fallbackSnap = await getDoc(fallbackRef);
          if (cancelled) return;
          if (fallbackSnap.exists()) {
            if (!cancelled) setEntries(parseEntries(fallbackSnap.data()));
            return;
          }
        }
        if (!cancelled) setEntries([]);
      })
      .catch(() => {
        if (!cancelled) setEntries([]);
      });

    return () => { cancelled = true; };
  }, [lang, bookId, chapter]);

  return { entries };
};
